/**
 * Source values for metadata
 */
export type MetadataSource =
  | 'locations'
  | 'freight_regions'
  | 'products'
  | 'markets'
  | 'ratebooks'
  | 'counterparties'
  | 'reason_codes'
  | 'prices'
  | 'users'
  | 'tractors'
  | 'trailers'
  | 'load_numbers'

/**
 * Format values for metadata
 */
export type MetadataFormat =
  | 'full'
  | 'id_name'
  | 'product_groups'
  | 'by_product_group'
  | 'all_options'
  | 'ratebooks'
  | 'basic_profile'
  | 'codes'
  | 'processes'
  | 'numbers'
  | 'contracts'
  | 'alternates'

/**
 * Location types for metadata
 */
export type LocationType = 'Terminal' | 'Store' | 'Supplier'

export type IdNameAddressResult = {
  id: string
  name: string
  address?: string
  city?: string
  state?: string
}

export type IdNameResult = {
  id: string
  name: string
}

/**
 * Parameter for metadata API requests
 */
export interface MetadataParam {
  source: MetadataSource
  format: MetadataFormat
  type?: LocationType
  processes?: string[]
  additionalProp1?: Record<string, unknown>
  product_ids?: string[]
}

/**
 * Configuration for metadata API requests
 */
export interface MetadataConfig {
  params: {
    [key: string]: MetadataParam
  }
  market?: string
}

/**
 * Response type for supplier config metadata
 */
export interface SupplierConfigMetadataResponse {
  locations: Array<{ id: string; name: string }>
  product_hierarchy: {
    [key: string]: Array<{ id: string; name: string }>
  }
  users: Array<{
    id: string
    username: string
    name: string
    email: string
  }>
}

/**
 * Ratebook metadata configuration
 */
export const ratebookMetadata: MetadataConfig = {
  params: {
    origins: {
      source: 'locations',
      format: 'full',
      type: 'Terminal',
    },
    destinations: {
      source: 'locations',
      format: 'full',
      type: 'Store',
    },
    freight_regions: {
      source: 'freight_regions',
      format: 'id_name',
    },
    product_groups: {
      source: 'products',
      format: 'product_groups',
    },
    product_hierarchy: {
      source: 'products',
      format: 'by_product_group',
    },
    markets: {
      source: 'markets',
      format: 'id_name',
    },
    ratebook_options: {
      source: 'ratebooks',
      format: 'all_options',
    },
  },
  market: '',
}

/**
 * Ratebook creation metadata configuration
 */
export const ratebookCreationMetadata: MetadataConfig = {
  params: {
    parties: {
      source: 'counterparties',
      format: 'ratebooks',
    },
    options: {
      source: 'ratebooks',
      format: 'all_options',
    },
  },
  market: '',
}

/**
 * Individual reason code object
 */
export interface ReasonCode {
  id: string
  reason_id: string
  reason: string
  allow_system_override: boolean
  in_cab_create_outage: boolean
  processes: string[]
}

/**
 * Terminal options for terminal mapping
 */
export const terminalOptionsMetadata: MetadataConfig = {
  params: {
    locations: {
      source: 'locations',
      format: 'id_name',
      type: 'Terminal',
    },
  },
}

/**
 * Store options for store mapping
 */
export const storeOptionsMetadata: MetadataConfig = {
  params: {
    locations: {
      source: 'locations',
      format: 'id_name',
      type: 'Store',
    },
  },
}

/**
 * Product options for product mapping
 */
export const productOptionsMetadata: MetadataConfig = {
  params: {
    product_hierarchy: {
      source: 'products',
      format: 'by_product_group',
    },
  },
}

/**
 * Supplier options for supplier mapping
 */
export const supplierOptionsMetadata: MetadataConfig = {
  params: {
    suppliers: {
      source: 'counterparties',
      format: 'id_name',
      type: 'Supplier',
    },
  },
}

/**
 * Supplier config metadata configuration
 */
export const supplierConfigMetadata: MetadataConfig = {
  params: {
    locations: {
      source: 'locations',
      format: 'id_name',
      type: 'Terminal',
    },
    product_hierarchy: {
      source: 'products',
      format: 'by_product_group',
    },
    users: {
      source: 'users',
      format: 'basic_profile',
    },
  },
}

/**
 * Combined supplier reconciliation metadata configuration
 * Includes all metadata needed for MatchView: suppliers, terminals, products, states, and reason codes
 * Updated to fetch full terminal data for timezone resolution
 */
export const supplierReconciliationMetadata: MetadataConfig = {
  params: {
    suppliers: {
      source: 'counterparties',
      format: 'id_name',
      type: 'Supplier',
    },
    terminals: {
      source: 'locations',
      format: 'full',
      type: 'Terminal',
    },
    products: {
      source: 'products',
      format: 'by_product_group',
    },
    states: {
      source: 'locations',
      format: 'states' as MetadataFormat,
    },
    reason_codes: {
      source: 'reason_codes',
      format: 'codes',
      processes: ['supplier_reconciliation_approval', 'supplier_reconciliation_hold'],
    },
  },
}

/**
 * Individual reason code object
 */
export interface ReasonCode {
  id: string
  reason_id: string
  reason: string
  allow_system_override: boolean
  in_cab_create_outage: boolean
  processes: string[]
}

const scheduleConfigMetadata: MetadataConfig = {
  params: {
    tractors: {
      source: 'tractors',
      format: 'id_name',
    },
    trailers: {
      source: 'trailers',
      format: 'id_name',
    },
  },
}

const reasonCodes: MetadataConfig = {
  params: {
    codes: {
      source: 'reason_codes',
      format: 'codes',
      processes: ['driver_scheduling'],
    },
  },
}

const trailers: MetadataConfig = {
  params: {
    trailers: {
      source: 'trailers',
      format: 'id_name',
    },
  },
}
const tractors: MetadataConfig = {
  params: {
    tractors: {
      source: 'tractors',
      format: 'id_name',
    },
  },
}
export const supplyOptionsMetadata = (products: string[]) => ({
  params: {
    terminals: {
      source: 'locations',
      format: 'label_value',
      type: 'Terminal',
    },
    suppliers: {
      source: 'counterparties',
      format: 'label_value',
      type: 'Supplier',
    },
    contracts: {
      source: 'prices',
      format: 'contracts',
    },
    supply_owners: {
      source: 'prices',
      format: 'supply_owners',
    },
    price_types: {
      source: 'prices',
      format: 'price_types',
    },
    load_numbers: {
      source: 'load_numbers',
      format: 'numbers',
    },
    alternates: {
      source: 'products',
      format: 'alternates',
      product_names: products,
    },
  },
})

export const sectorDepotMetadata = {
  params: {
    depots: {
      source: 'depots',
      format: 'full',
    },
  },
}

/**
 * Individual reason code object
 */
export interface ReasonCode {
  id: string
  reason_id: string
  reason: string
  allow_system_override: boolean
  in_cab_create_outage: boolean
  processes: string[]
}

/**
 * Terminal data with full location and timezone information
 */
export interface TerminalMetadata {
  id: string
  name: string
  short_name?: string
  timezone?: string
  address?: string
  city?: string
  state?: string
  market?: string
  type: 'Terminal'
  // Additional fields from the API that we don't need to type fully
  [key: string]: any
}

/**
 * Combined response type for supplier reconciliation metadata
 * Includes all data needed for MatchView components with full terminal data for timezone resolution
 */
export interface SupplierReconciliationMetadataResponse {
  suppliers: Array<{ id: string; name: string }>
  terminals: Array<{ id: string; name: string; timezone: string }>
  products: {
    [key: string]: Array<{ id: string; name: string; code?: string }>
  }
  states: Array<{ value: string; label: string }>
  reason_codes: ReasonCode[]
}

export type MetadataItem = { label?: string; value?: string; name?: string; id?: string } | string
export type MetadataResponse = {
  [key: string]: MetadataItem[] | Record<string, MetadataItem[]>
}

/**
export interface SupplierReconciliationMetadataResponse {
 * Configured parameter sets for different API use cases
 */
export const configuredParams = {
  ratebook_options: ratebookMetadata,
  ratebook_creation: ratebookCreationMetadata,
  terminal_options: terminalOptionsMetadata,
  store_options: storeOptionsMetadata,
  product_options: productOptionsMetadata,
  supplier_options: supplierOptionsMetadata,
  supplier_config: supplierConfigMetadata,
  supplier_reconciliation: supplierReconciliationMetadata,
  schedule: scheduleConfigMetadata,
  tractors,
  trailers,
  reasonCodes,
}
